/**
 * Every dashboard API path in one place, relative to VITE_API_BASE_URL
 * (default `/api/dashboard`). Mirrors apps/mobile/src/constants/apiUrl.ts.
 *
 * Paths that look odd are odd on the server too — see the notes.
 */
export const API = {
  auth: {
    login: "/auth/login",
    logout: "/auth/logout",
    logoutAllDevices: "/auth/logout-all-devices",
    refresh: "/auth/refresh",
    resetPassword: "/auth/reset-password",
    me: "/auth/me",
  },

  users: {
    list: "/users",
    create: "/users",
    detail: (id: string) => `/users/${id}`,
    update: (id: string) => `/users/${id}`,
    archive: (id: string) => `/users/${id}/archive`,
    resendActivation: (id: string) => `/users/${id}/resend-activation`,
    /** Both the authenticated change and the reset-token flow. */
    password: "/users/password",
    /** Is an emailed link still usable? Asks without spending the token. */
    verifyPasswordToken: "/users/password/verify-token",
  },

  analysis: {
    /** Keyed by the ENROLMENT, not the patient: a score belongs to a study. */
    patientStudy: (patientStudyId: string) =>
      `/analysis/patient-studies/${patientStudyId}`,
    patientMonitoring: (patientId: string) =>
      `/analysis/patients/${patientId}/monitoring`,
    /** A whole cohort, one questionnaire at a time — the questionnaire is a
     *  required query parameter, not part of the path. */
    study: (studyId: string) => `/analysis/studies/${studyId}`,
    plugins: "/analysis/plugins",
    installPlugin: (pluginId: string) => `/analysis/plugins/${pluginId}/install`,
  },

  patients: {
    list: "/patients",
    create: "/patients",
    detail: (id: string) => `/patients/${id}`,
    update: (id: string) => `/patients/${id}`,
    archive: (id: string) => `/patients/${id}/archive`,
    delete: (id: string) => `/patients/${id}`,
    /** Questionnaires scheduled for one patient, outside any study. */
    surveys: (patientId: string) => `/patients/${patientId}/surveys`,
    stopSurvey: (patientId: string, scheduleId: string) =>
      `/patients/${patientId}/surveys/${scheduleId}/stop`,
    triggerSurvey: (patientId: string, scheduleId: string) =>
      `/patients/${patientId}/surveys/${scheduleId}/trigger`,
    /** Texts the open occurrence's link again. Opens nothing. */
    resendSurvey: (patientId: string, scheduleId: string) =>
      `/patients/${patientId}/surveys/${scheduleId}/resend`,
    /**
     * What a patient answered, one submitted occurrence at a time. Under
     * /patients even for a study's questionnaire: a submission is
     * (patient, schedule, occurrence) and a schedule need not belong to a
     * study, so there is one read rather than one per kind of owner.
     */
    surveyResponse: (
      patientId: string,
      scheduleId: string,
      occurrenceKey: string,
    ) =>
      `/patients/${patientId}/surveys/${scheduleId}/responses/${encodeURIComponent(occurrenceKey)}`,
  },

  studies: {
    list: "/studies",
    create: "/studies",
    detail: (id: string) => `/studies/${id}`,
    update: (id: string) => `/studies/${id}`,
    status: (id: string) => `/studies/${id}/status`,
    delete: (id: string) => `/studies/${id}`,
    // Keyed by the ScheduledSurvey row, not the survey: one study can hold
    // the same survey several times on different schedules.
    addSurvey: (id: string) => `/studies/${id}/surveys`,
    removeScheduledSurvey: (studyId: string, scheduledSurveyId: string) =>
      `/studies/${studyId}/surveys/${scheduledSurveyId}`,
    updateScheduledSurvey: (studyId: string, scheduledSurveyId: string) =>
      `/studies/${studyId}/surveys/${scheduledSurveyId}`,
    /** The signed consent as a PDF, built server-side. */
    consentPdf: (studyId: string, patientId: string) =>
      `/studies/${studyId}/patients/${patientId}/consent/pdf`,
    triggerScheduledSurvey: (
      studyId: string,
      patientId: string,
      scheduledSurveyId: string,
    ) =>
      `/studies/${studyId}/patients/${patientId}/surveys/${scheduledSurveyId}/trigger`,
    enroll: (id: string) => `/studies/${id}/enroll`,
    unenroll: (studyId: string, patientId: string) =>
      `/studies/${studyId}/patients/${patientId}`,
    consent: (studyId: string, patientId: string) =>
      `/studies/${studyId}/patients/${patientId}/consent`,
    patients: (studyId: string) => `/studies/${studyId}/patients`,
    /** Yes, really: /studies/patients/:id/studies. Server-side path. */
    ofPatient: (patientId: string) => `/studies/patients/${patientId}/studies`,
    /** A study's people: list + upsert on one path, remove by user. */
    members: (studyId: string) => `/studies/${studyId}/members`,
    member: (studyId: string, userId: string) =>
      `/studies/${studyId}/members/${userId}`,
    /** Private (members only) vs public (view-only for the whole facility). */
    visibility: (studyId: string) => `/studies/${studyId}/visibility`,
  },

  surveys: {
    list: "/surveys",
    /** The only way to create a survey: upload a "procare-survey" JSON file. */
    import: "/surveys/import",
    detail: (id: string) => `/surveys/${id}`,
  },

  hospital: "/hospital",

  /**
   * The two logs. Separate paths because they answer to different people and
   * different capabilities: the audit trail to a data manager or an inspector,
   * the access log to a DPO or incident response.
   */
  audit: "/audit",
  accessLog: "/access-log",
} as const;
